import { useState, useEffect, useRef } from "react";
import { callApi } from "../utils/ApiCall";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MobileStepper from "@mui/material/MobileStepper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
// Removed overlay chevron buttons
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

export const ImageCarousel = ({ open, productCode, handleClose }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [images, setImages] = useState([]); // array of object URLs
  const [loading, setLoading] = useState(false);
  const objectUrlsRef = useRef([]); // track created URLs for cleanup
  const mountedRef = useRef(false);
  const cancelRef = useRef(false);
  const [side, setSide] = useState(512); // dynamic square side length
  const decodedRef = useRef(new Set()); // track decoded image indices

  // track mount status
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
    };
  }, []);

  // compute optimal square size based on viewport
  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Reserve vertical space for title + stepper (~160-180px). Horizontal padding ~96px (dialog margins)
      const maxSideByHeight = vh - 200; // leave breathing room
      const maxSideByWidth = vw - 96;
      const s = Math.min(1024, maxSideByHeight, maxSideByWidth);
      setSide(Math.max(220, s));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const fetchImages = async (code) => {
    const MAX_IMAGES = 20;
    const collected = [];
    setLoading(true);
    cancelRef.current = false;
    try {
      for (let i = 1; i <= MAX_IMAGES; i++) {
        if (cancelRef.current) break;
        // Multi-format attempt per index
        const formats = ["webp", "png"];
        let blobFound = null;
        for (const ext of formats) {
          const fileName = `${code}-${i}.${ext}`;
          try {
            const res = await callApi({
              method: "get",
              url: `product-images/${fileName}`,
              responseType: "blob",
              headers: { Accept: "image/webp,image/png,*/*" },
            });
            if (res?.data instanceof Blob && res.data.size > 0) {
              blobFound = res.data;
              break; // stop trying formats for this index
            }
          } catch (e) {
            // try next format
            continue;
          }
        }
        if (!blobFound) {
          // no format succeeded => assume no further numbered images
          break;
        }
        const url = URL.createObjectURL(blobFound);
        objectUrlsRef.current.push(url);
        collected.push(url);
        if (mountedRef.current) setImages([...collected]);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // load when dialog opens / product changes
  useEffect(() => {
    if (open && productCode) {
      // reset previous
      setImages([]);
      setActiveStep(0);
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
      fetchImages(productCode);
    } else if (!open) {
      // when closed revoke object URLs
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
      setImages([]);
      setActiveStep(0);
    }
  }, [open, productCode]);

  const handleNext = () =>
    setActiveStep((prev) => Math.min(prev + 1, images.length - 1));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleDialogClose = (event, reason) => {
    // Allow backdrop & escape closure (MUI passes reason: 'backdropClick' | 'escapeKeyDown')
    cancelRef.current = true; // signal any in-flight fetch loop to stop
    objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    objectUrlsRef.current = [];
    decodedRef.current.clear();
    setImages([]);
    setActiveStep(0);
    handleClose();
  };

  // Preload (decode) upcoming images for smoother transitions
  useEffect(() => {
    if (!images.length) return;
    const toPreload = [activeStep + 1, activeStep + 2]; // preload next two if available
    toPreload.forEach((idx) => {
      if (idx < images.length && !decodedRef.current.has(idx)) {
        const img = new Image();
        img.src = images[idx];
        // Use decode() when supported for async decode without layout
        const markDecoded = () => decodedRef.current.add(idx);
        if (img.decode) {
          img.decode().then(markDecoded).catch(markDecoded); // mark even on error to avoid loops
        } else {
          img.onload = markDecoded;
          img.onerror = markDecoded;
        }
      }
    });
  }, [activeStep, images]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose} // enables click-away (backdrop) & escape key closing
      aria-describedby="product-image-dialogue"
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: { sx: { p: 0 } },
      }}
    >
      <Box
        sx={{ position: "relative", display: "flex", flexDirection: "column" }}
      >
        <DialogTitle sx={{ py: 1, pr: 6 }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              {productCode}
            </Typography>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "background.default",
            px: 2,
            pb: 1,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: side,
              height: side,
              maxWidth: "100%",
              maxHeight: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {images.length > 0 ? (
              <Fade in timeout={300} key={activeStep}>
                <Box
                  component="img"
                  src={images[activeStep]}
                  alt={productCode}
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                    borderRadius: 1,
                    boxShadow: 1,
                    userSelect: "none",
                  }}
                />
              </Fade>
            ) : (
              <Box
                sx={{
                  p: 2,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {loading ? "Loading images..." : "No Images"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <MobileStepper
            variant="dots"
            steps={Math.max(images.length, 1)}
            position="static"
            activeStep={Math.min(activeStep, Math.max(images.length - 1, 0))}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
                disabled={
                  activeStep === images.length - 1 || images.length === 0
                }
              >
                Next{" "}
                {theme.direction === "rtl" ? (
                  <KeyboardArrowLeft />
                ) : (
                  <KeyboardArrowRight />
                )}
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handleBack}
                disabled={activeStep === 0 || images.length === 0}
              >
                {theme.direction === "rtl" ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                )}{" "}
                Back
              </Button>
            }
          />
        </Box>
      </Box>
    </Dialog>
  );
};
